import { ImageOutlined } from '@mui/icons-material';
import {
  Checkbox,
  MenuItem,
  TableCell,
  TextField,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { CodeInfo } from '../../../../../../services/apiTypes';
import {
  PARTS_SELECTION_FONT_SIZE,
  TEXT_OVERFLOW_2LINES,
} from '../../../../../../_constants/styleConst';
import React from 'react';
import { tableHeaders } from '../../SectionTable';
import {
  MIN_PARTS_AMOUNT,
  PARTS_ATTRIBUTE,
} from '../../../../../../_constants/inspectionConst';

export type SectionPartInfo = {
  listNo: string;
  inFigureNo: string;
  partsAttribute: string;
  isSelect: boolean;
  selectedPartsCode: string;
  partsList: {
    partsCode: string;
    name: string;
  }[];
  amount: number;
  r: CodeInfo[];
  isometricDrawing: string;
  ecnNo: string;
};

type SectionPartRowProps = {
  index: number;
  sectionName: string;
  part: SectionPartInfo;
  onChangeSelection: (
    isChecked: boolean,
    listNo: string,
    inFigureNo: string,
  ) => void;
  onChangePartsCode: (
    newPartsCode: string,
    listNo: string,
    inFigureNo: string,
  ) => void;
  onChangeAmount: (
    newAmount: string,
    listNo: string,
    inFigureNo: string,
  ) => void;
  handlePartsListImage: (isometricDrawing: string, section: string) => void;
};

/** セクションに紐づくパーツ1つ分 */
const SectionPartRow: React.FC<SectionPartRowProps> = ({
  index,
  sectionName,
  part: {
    isSelect,
    partsAttribute,
    selectedPartsCode,
    partsList,
    amount,
    r,
    listNo,
    inFigureNo,
    isometricDrawing,
    ecnNo,
  },
  onChangeSelection,
  onChangePartsCode,
  onChangeAmount,
  handlePartsListImage,
}) => {
  /** 下限値を下回らないように制御 */
  const adjustAmount = () => {
    if (amount < MIN_PARTS_AMOUNT) {
      onChangeAmount(MIN_PARTS_AMOUNT.toString(), listNo, inFigureNo);
    }
  };

  return (
    <React.Fragment>
      {/* チェックボックス */}
      <TableCell
        sx={{
          p: tableHeaders[2].p,
          width: tableHeaders[2].width,
          minWidth: tableHeaders[2].minWidth,
        }}
        colSpan={3} // アイコン + セクション名
        align="right"
      >
        <Checkbox
          id={`RCode_PartsCode_checkbox_${index}`}
          sx={{ '& .MuiSvgIcon-root': { fontSize: 25 } }}
          disabled={partsAttribute === PARTS_ATTRIBUTE.MUST}
          checked={partsAttribute === PARTS_ATTRIBUTE.MUST || isSelect}
          onChange={(e) => {
            onChangeSelection(e.target.checked, listNo, inFigureNo);
          }}
        />
      </TableCell>
      {/* パーツコード */}
      <TableCell
        sx={{
          p: tableHeaders[3].p,
          px: '5px', // テキストボックスの余白文小さくする
          width: tableHeaders[3].width,
          minWidth: tableHeaders[3].minWidth,
          textAlign: tableHeaders[3].textAlign,
        }}
      >
        <TextField
          id={`RCode_selectedPartsCode_${index}`}
          size="small"
          variant="outlined"
          fullWidth
          select={partsList.length >= 2}
          slotProps={{
            input: {
              readOnly: partsList.length === 1,
              style: { fontSize: PARTS_SELECTION_FONT_SIZE },
            },
          }}
          value={selectedPartsCode}
          onChange={(e) => {
            onChangePartsCode(e.target.value, listNo, inFigureNo);
          }}
        >
          {partsList.length > 1 &&
            partsList.map((part, i) => (
              <MenuItem
                key={`part_code_${i}`}
                value={part.partsCode}
                sx={{ fontSize: PARTS_SELECTION_FONT_SIZE }}
              >
                {part.partsCode}
              </MenuItem>
            ))}
        </TextField>
      </TableCell>
      {/* 部品名 */}
      <TableCell
        sx={{
          p: tableHeaders[4].p,
          width: tableHeaders[4].width,
          minWidth: tableHeaders[4].minWidth,
          textAlign: tableHeaders[4].textAlign,
        }}
      >
        <Typography
          sx={[TEXT_OVERFLOW_2LINES, { fontSize: PARTS_SELECTION_FONT_SIZE }]}
        >
          {partsList.find((part) => part.partsCode === selectedPartsCode)
            ?.name ?? ''}
        </Typography>
      </TableCell>
      {/* キー番号(図中番号) */}
      <TableCell
        key={'parts_key_no'}
        sx={{
          p: tableHeaders[5].p,
          width: tableHeaders[5].width,
          minWidth: tableHeaders[5].minWidth,
          textAlign: tableHeaders[5].textAlign,
        }}
      >
        <Typography sx={{ fontSize: PARTS_SELECTION_FONT_SIZE }}>
          {inFigureNo}
        </Typography>
      </TableCell>
      {/* 発注量 */}
      <TableCell
        key={'parts_amount'}
        sx={{
          p: tableHeaders[6].p,
          width: tableHeaders[6].width,
          minWidth: tableHeaders[6].minWidth,
          textAlign: tableHeaders[6].textAlign,
        }}
      >
        <TextField
          id={`RCode_amount_${index}`}
          type="number"
          size="small"
          variant="outlined"
          fullWidth
          slotProps={{
            htmlInput: { min: MIN_PARTS_AMOUNT },
            input: { style: { fontSize: PARTS_SELECTION_FONT_SIZE } },
          }}
          // 値を消した時0になるので一時的に空表示にする
          value={amount === 0 ? '' : amount}
          onChange={(e) => {
            onChangeAmount(e.target.value, listNo, inFigureNo);
          }}
          // 空で確定できないように制御
          onBlur={adjustAmount}
        />
      </TableCell>
      {/* Rコード */}
      <TableCell
        sx={{
          p: tableHeaders[7].p,
          width: tableHeaders[7].width,
          minWidth: tableHeaders[7].minWidth,
          textAlign: tableHeaders[7].textAlign,
        }}
      >
        <Typography
          sx={{ fontSize: PARTS_SELECTION_FONT_SIZE, whiteSpace: 'pre-wrap' }}
        >
          {r.map((data) => data.code).join('\n')}
        </Typography>
      </TableCell>
      {/* Rコード詳細 */}
      <TableCell
        sx={{
          p: tableHeaders[8].p,
          width: tableHeaders[8].width,
          minWidth: tableHeaders[8].minWidth,
          textAlign: tableHeaders[8].textAlign,
        }}
      >
        <Typography
          sx={{ whiteSpace: 'pre', fontSize: PARTS_SELECTION_FONT_SIZE }}
        >
          {r.map((data) => data.detail).join('\n')}
        </Typography>
      </TableCell>
      {/* パーツリストアイコン */}
      <TableCell
        sx={{
          p: tableHeaders[9].p,
          width: tableHeaders[9].width,
          minWidth: tableHeaders[9].minWidth,
          textAlign: tableHeaders[9].textAlign,
          fontSize: PARTS_SELECTION_FONT_SIZE,
        }}
      >
        <Link
          to="#"
          onClick={() => handlePartsListImage(isometricDrawing, sectionName)}
        >
          <ImageOutlined
            sx={{
              display: isometricDrawing ? 'flex' : 'none',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              opacity: 0.7,
              color: 'black',
              cursor: 'pointer',
              pointerEvents: 'auto',
              '&:hover': {
                transform: 'scale(1.2)',
                opacity: 1,
              },
            }}
          />
        </Link>
      </TableCell>
      {/* ECNファイル */}
      <TableCell
        sx={{
          p: tableHeaders[10].p,
          width: tableHeaders[10].width,
          minWidth: tableHeaders[10].minWidth,
          textAlign: tableHeaders[10].textAlign,
        }}
      >
        <Typography sx={{ fontSize: PARTS_SELECTION_FONT_SIZE }}>
          {ecnNo}
        </Typography>
      </TableCell>
    </React.Fragment>
  );
};
export default SectionPartRow;
